/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { EventType } from "@azure/msal-browser";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "pages/Sidenav";
import Configurator from "pages/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import Welcome from "components/Welcome";

import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
// Material Dashboard 2 React routes
import buildRouteConfig from "routes";
import RiskAdvisorWidget from "components/RiskAdvisorWidget";
import { ScreenContextProvider } from "context/ScreenContext";
import buildSettingsRouteConfig from "settingsroutes";

// Material Dashboard 2 React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setSidenavHover,
  setOpenConfigurator,
} from "context";
import { UserControllerProvider, useCurrentUser } from "context/UserContext";
import usePageViewBeacon from "hooks/usePageViewBeacon";
import { useNativeAuth, NativeAuthProvider } from "context/NativeAuthContext";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import ConfigureTaxonomy from "pages/Taxonomy/ConfigureTaxonomy";
import Taxonomy from "pages/Taxonomy";
import CategoryDetails from "pages/ProtectDashboard/CategoryDetails";
import SubCategoryDetails from "pages/ProtectDashboard/SubCategoryDetails";
import ExposureDetails from "pages/ProtectDashboard/ExposureDetails";
import ProtectDashboard from "pages/ProtectDashboard/ProtectDashboard";
import ExposureDetSubCat from "pages/ProtectDashboard/ExposureDetSubCat";
import TenantUsers from "pages/Users/TenantUsers";
import SignUp from "pages/SignUp/SignUp";
import SignIn from "pages/SignIn/SignIn";
import AccessDenied from "pages/AccessDenied";
import CircularProgress from "@mui/material/CircularProgress";
import { compareIssuingPolicy } from "./utils/claimUtils";
import { b2cPolicies, protectedResources, useNativeAuthForSignIn } from "./authConfig";

import { Home } from "./pages/Home";
import { TodoList } from "./pages/TodoList";
import { PageLayout } from "./components/PageLayout";

function Pages() {
  /**
   * useMsal is hook that returns the PublicClientApplication instance,
   * an array of all accounts currently signed in and an inProgress value
   * that tells you what msal is currently doing. For more, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
   */
  const { instance } = useMsal();
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event.payload.account
      ) {
        const { idTokenClaims } = event.payload;
        if (!idTokenClaims) return;
        /**
         * B2C only: "tfp" / "acr" policy checks. Skip for Entra ID (no tfp/acr).
         * https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
         */
        if (idTokenClaims.tfp != null || idTokenClaims.acr != null) {
          if (compareIssuingPolicy(idTokenClaims, b2cPolicies.names.editProfile)) {
            // retrieve the account from initial sing-in to the app
            const originalSignInAccount = instance
              .getAllAccounts()
              .find(
                (account) =>
                  account?.idTokenClaims &&
                  account.idTokenClaims.oid === idTokenClaims.oid &&
                  account.idTokenClaims.sub === idTokenClaims.sub &&
                  compareIssuingPolicy(account.idTokenClaims, b2cPolicies.names.signUpSignIn)
              );

            const signUpSignInFlowRequest = {
              authority: b2cPolicies.authorities.signUpSignIn.authority,
              account: originalSignInAccount,
            };

            // silently login again with the signUpSignIn policy
            instance.ssoSilent(signUpSignInFlowRequest);
          }

          /**
           * Below we are checking if the user is returning from the reset password flow.
           * If so, we will ask the user to reauthenticate with their new password.
           * If you do not want this behavior and prefer your users to stay signed in instead,
           * you can replace the code below with the same pattern used for handling the return from
           * profile edit flow
           */
          if (compareIssuingPolicy(idTokenClaims, b2cPolicies.names.forgotPassword)) {
            const signUpSignInFlowRequest = {
              authority: b2cPolicies.authorities.signUpSignIn.authority,
              scopes: [
                ...protectedResources.apiTodoList.scopes.read,
                ...protectedResources.apiTodoList.scopes.write,
              ],
            };
            instance.loginRedirect(signUpSignInFlowRequest);
          }
        }
      }

      if (event.eventType === EventType.LOGIN_FAILURE) {
        // Check for forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (event.error && event.error.errorMessage.includes("AADB2C90118")) {
          const resetPasswordRequest = {
            authority: b2cPolicies.authorities.forgotPassword.authority,
            scopes: [],
          };
          instance.loginRedirect(resetPasswordRequest);
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
    // eslint-disable-next-line
  }, [instance]);

  return (
    <Routes>
      <Route path="/todolist" element={<TodoList />} />
      <Route path="/" element={<Home />} />
    </Routes>
  );
}

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
function filterRoutesByRole(routesList, role) {
  if (!routesList) return routesList;
  return routesList
    .map((route) => {
      if (route.roles && !route.roles.includes(role)) return null;
      const copy = { ...route };
      if (copy.collapse) {
        copy.collapse = copy.collapse.filter((r) => !r.roles || r.roles.includes(role));
      }
      return copy;
    })
    .filter(Boolean);
}

/**
 * When user is not in MSAL but has native auth tokens, show dashboard. Otherwise show sign-in/sign-up or MSAL Welcome.
 * useNativeAuthForSignIn: true = custom native auth (email/password); false = MSAL SSO (Welcome triggers redirect).
 */
function UnauthenticatedOrNativeContent({
  controller,
  dispatch,
  getRoutes,
  handleOnMouseEnter,
  handleOnMouseLeave,
  handleConfiguratorOpen,
  useNativeAuth: useNativeAuthProp,
}) {
  const { hasValidToken } = useNativeAuth();
  // Dynamic config from per-tenant bootstrap takes precedence; fall back to static build-time value.
  const shouldUseNativeAuth =
    useNativeAuthProp !== undefined ? useNativeAuthProp : useNativeAuthForSignIn;

  if (hasValidToken) {
    return (
      <UserControllerProvider>
        <AuthenticatedDashboard
          controller={controller}
          dispatch={dispatch}
          getRoutes={getRoutes}
          handleOnMouseEnter={handleOnMouseEnter}
          handleOnMouseLeave={handleOnMouseLeave}
          handleConfiguratorOpen={handleConfiguratorOpen}
        />
      </UserControllerProvider>
    );
  }
  if (shouldUseNativeAuth) {
    return (
      <Routes>
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="*" element={<SignIn />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/signup" element={<SignUp />} />
      <Route path="/signin" element={<Welcome />} />
      <Route path="*" element={<Welcome />} />
    </Routes>
  );
}

function AuthenticatedDashboard({
  controller,
  dispatch,
  getRoutes,
  handleOnMouseEnter,
  handleOnMouseLeave,
  handleConfiguratorOpen,
}) {
  const { role, isRiskAdmin, modules } = useCurrentUser();
  // Record screen navigation for the Super Risk Admin "User Activity" audit trail.
  usePageViewBeacon();
  const { openConfigurator, layout, transparentSidenav, whiteSidenav, darkMode } = controller;
  const filteredRoutes = useMemo(
    () => filterRoutesByRole(buildRouteConfig(modules), role),
    [modules, role]
  );
  const filteredSettingsRoutes = useMemo(
    () => filterRoutesByRole(buildSettingsRouteConfig(modules), role),
    [modules, role]
  );
  return (
    <ScreenContextProvider>
      {layout === "dashboard" && (
        <Sidenav
          color={controller.sidenavColor}
          brand={
            (controller.transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite
          }
          brandName="PAGERisk"
          routes={!openConfigurator ? filteredRoutes : filteredSettingsRoutes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      {layout === "vr" && <Configurator />}
      {/* Floating Risk Advisor (bottom-left) — screen-aware assistant available on every screen. */}
      <RiskAdvisorWidget />
      <Routes>
        {getRoutes(filteredRoutes)}
        {getRoutes(filteredSettingsRoutes)}
        <Route path="/" element={<Navigate to="/home" />} />
        <Route path="/configure/:id?" element={<ConfigureTaxonomy />} />
        <Route path="/Taxonomy" element={<Taxonomy />} />
        <Route path="/overview" element={<ProtectDashboard />} />
        <Route path="/overview/category" element={<CategoryDetails />} />
        <Route path="/overview/category/subcategory" element={<SubCategoryDetails />} />
        <Route path="/overview/category/exposure" element={<ExposureDetails />} />
        <Route path="/overview/category/subcategory/exposure" element={<ExposureDetSubCat />} />
        <Route path="/users" element={<TenantUsers />} />
        <Route
          path="/AnticpateSetting"
          element={<Navigate to="/anticipate/user-search" replace />}
        />
      </Routes>
    </ScreenContextProvider>
  );
}

/**
 * Gate between "authenticated" (has a valid token) and "authorized" (is a member of this workspace).
 * MSAL's <AuthenticatedTemplate> only proves the former. A non-member must never see the app shell —
 * they get the explicit AccessDenied screen instead. While /users/me is in flight we show a spinner
 * so there's no flash of an empty dashboard.
 */
function DashboardGate(props) {
  const { loading, access } = useCurrentUser();
  if (loading || access === "loading") {
    return (
      <MDBox
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress color="info" />
      </MDBox>
    );
  }
  if (access !== "ok") {
    return <AccessDenied reason={access} />;
  }
  return <AuthenticatedDashboard {...props} />;
}

function App({ instance, tenantAuthConfig }) {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    sidenavHover,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Hover fly-out disabled: the sidenav is now click-to-pin (toggle via the hamburger), so it never
  // expands/collapses on hover. This removes the accidental-expand + hard-to-click problem and keeps
  // the content width constant. Kept as no-ops so the props passed down stay valid.
  const handleOnMouseEnter = () => {};
  const handleOnMouseLeave = () => {};

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return [<Route exact path={route.route} element={route.component} key={route.key} />];
      }

      return [];
    });

  return direction === "rtl" ? (
    <MsalProvider instance={instance}>
      <CacheProvider value={rtlCache}>
        <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
          <CssBaseline />
          {layout === "dashboard" && (
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="PAGERisk"
              routes={[]}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          )}
          {layout === "vr" && <Configurator />}
          <Routes>
            {getRoutes([])}
            <Route path="*" element={<Navigate to="/home" />} />
          </Routes>
        </ThemeProvider>
      </CacheProvider>
    </MsalProvider>
  ) : (
    <MsalProvider instance={instance}>
      <NativeAuthProvider>
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <CssBaseline />
          <UnauthenticatedTemplate>
            <UnauthenticatedOrNativeContent
              controller={controller}
              dispatch={dispatch}
              getRoutes={getRoutes}
              handleOnMouseEnter={handleOnMouseEnter}
              handleOnMouseLeave={handleOnMouseLeave}
              handleConfiguratorOpen={handleConfiguratorOpen}
              useNativeAuth={tenantAuthConfig?.useNativeAuth}
            />
          </UnauthenticatedTemplate>
          <AuthenticatedTemplate>
            <UserControllerProvider>
              <DashboardGate
                controller={controller}
                dispatch={dispatch}
                getRoutes={getRoutes}
                handleOnMouseEnter={handleOnMouseEnter}
                handleOnMouseLeave={handleOnMouseLeave}
                handleConfiguratorOpen={handleConfiguratorOpen}
              />
            </UserControllerProvider>
          </AuthenticatedTemplate>
        </ThemeProvider>
      </NativeAuthProvider>
    </MsalProvider>
  );
  /*   return (
    <MsalProvider instance={instance}>
      <PageLayout>
        <Pages />
      </PageLayout>
    </MsalProvider>
  ); */
}
export default App;
